import React from 'react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withTranslation, WithTranslation } from 'react-i18next';
import { AnchorBlock } from '../anchorBlock';
import CoreAPIService from '../../utils/core-api';
import DateUtils from '../../utils/dateUtils';

/** One row of cabcom's `GET /reports/user/:uuid`. */
export interface SentMessage {
    message_uuid: string;
    template_key_name: string | null;
    template_name: string | null;
    template_description: string | null;
    reference: string | null;
    provider: string;
    status: string;
    created_at: string;
}

const PAGE_SIZE = 20;

/** cabcom providers, one per channel. */
const PROVIDERS = ['awsses', 'vonage', 'fcm'];

interface SentMessagesProps extends WithTranslation {
    /** iD uuid of the recipient (driver, rider or manager). */
    uuid?: string;
}

/**
 * Emails, SMS and pushes cabcom sent to a user, newest first.
 * Sends made directly by the webservice are not logged by cabcom and are missing (LT-2284).
 */
const SentMessages = ({ t, uuid }: SentMessagesProps): JSX.Element => {
    const [messages, setMessages] = React.useState<SentMessage[]>([]);
    const [hasMore, setHasMore] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [providers, setProviders] = React.useState<string[]>(PROVIDERS);

    const load = React.useCallback(
        (offset: number) => {
            if (!uuid) {
                return;
            }
            setLoading(true);
            setError(null);
            // Filtered by cabcom so that pages stay full. One extra row tells whether another page exists.
            const provider = providers.length < PROVIDERS.length ? `&provider=${providers.join(',')}` : '';
            CoreAPIService.get(`/cabcom/reports/user/${uuid}?limit=${PAGE_SIZE + 1}&offset=${offset}${provider}`)
                .then(({ payload }: { payload: SentMessage[] }) => {
                    const page = Array.isArray(payload) ? payload : [];
                    setMessages((previous) => [...(offset ? previous : []), ...page.slice(0, PAGE_SIZE)]);
                    setHasMore(page.length > PAGE_SIZE);
                })
                .catch((e) => setError(e?.message || String(e)))
                .finally(() => setLoading(false));
        },
        [uuid, providers],
    );

    React.useEffect(() => {
        setMessages([]);
        setHasMore(false);
        load(0);
    }, [load]);

    const toggleProvider = (provider: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        // At least one channel stays selected: an empty filter would read as "everything".
        setProviders((previous) =>
            checked
                ? PROVIDERS.filter((p) => p === provider || previous.includes(p))
                : previous.length > 1
                  ? previous.filter((p) => p !== provider)
                  : previous,
        );
    };

    const filters = uuid ? (
        <div>
            {PROVIDERS.map((provider) => (
                <FormControlLabel
                    key={provider}
                    control={
                        <Checkbox
                            size="small"
                            color="primary"
                            checked={providers.includes(provider)}
                            disabled={providers.length === 1 && providers.includes(provider)}
                            onChange={toggleProvider(provider)}
                        />
                    }
                    label={t(`channel.${provider}`)}
                />
            ))}
        </div>
    ) : undefined;

    const renderRow = (message: SentMessage): JSX.Element => (
        <TableRow key={message.message_uuid}>
            <TableCell>{DateUtils.tsToDate(message.created_at as any)}</TableCell>
            <TableCell>{t(`channel.${message.provider}`, { defaultValue: message.provider })}</TableCell>
            <TableCell>
                <div>{message.template_name || message.template_key_name || '-'}</div>
                {message.template_description && (
                    <div style={{ fontSize: 12, color: '#757575' }}>{message.template_description}</div>
                )}
            </TableCell>
            <TableCell>{message.reference || '-'}</TableCell>
            <TableCell>{t(`status.${message.status}`, { defaultValue: message.status })}</TableCell>
        </TableRow>
    );

    let items: JSX.Element | JSX.Element[] = [];
    if (!uuid) {
        items = <div>{t('missing_uuid')}</div>;
    } else if (error) {
        items = <div>{t('load_error', { message: error })}</div>;
    } else if (messages.length > 0) {
        // The "load more" button is part of the items: AnchorBlock renders its `action` above them.
        items = (
            <>
                <Table size="small" aria-label={t('title')}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('columns.date')}</TableCell>
                            <TableCell>{t('columns.channel')}</TableCell>
                            <TableCell>{t('columns.message')}</TableCell>
                            <TableCell>{t('columns.reference')}</TableCell>
                            <TableCell>{t('columns.status')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{messages.map(renderRow)}</TableBody>
                </Table>
                {hasMore && (
                    <Button
                        color="secondary"
                        fullWidth
                        disabled={loading}
                        onClick={() => load(messages.length)}
                        style={{ marginTop: 8 }}
                    >
                        {t('load_more')}
                    </Button>
                )}
            </>
        );
    }

    return (
        <AnchorBlock
            id="sent_messages"
            title={t('title')}
            titleAction={filters}
            items={items}
            noValue={t('empty')}
            loader={loading && messages.length === 0 ? <CircularProgress size={24} /> : undefined}
        />
    );
};

export default withTranslation('sent_messages')(SentMessages);
